/**
 * In-memory MPC relay for fully-local Vultisig SecureVault DKG.
 *
 * The Vultisig SDK's seedphrase-based SecureVault creation (and its
 * `joinSecureVault` counterpart) shuttle DKLS / Schnorr keygen messages
 * through a relay server (default `https://api.vultisig.com/router`).
 * When WINBIT32 hosts every participating device in the same browser
 * tab there's nothing to ferry across the network — every message can
 * be passed in-process.
 *
 * `installInMemoryMpcRelay()` patches `globalThis.fetch` so any request
 * matching the configured relay base is served from in-memory state
 * that mirrors the real relay's REST shape:
 *
 *     POST   /<sessionId>                       — body: ["partyId"] (join)
 *     GET    /<sessionId>                       — list joined parties
 *     POST   /start/<sessionId>                 — body: ["dev1",…]
 *     POST   /complete/<sessionId>              — body: ["partyId"]
 *     GET    /complete/<sessionId>              — list completed parties
 *     POST   /complete/<sessionId>/keysign      — keysign completion (ack)
 *     POST   /setup-message/<sessionId>         — body: text (header `message_id`)
 *     GET    /setup-message/<sessionId>         — return body (header `message_id`)
 *     POST   /message/<sessionId>               — body: {from,to,body,hash,…}
 *     GET    /message/<sessionId>/<partyId>     — drain inbound queue
 *     DELETE /message/<sessionId>/<partyId>/<hash> — ack consumed message
 *
 * Every other URL passes through to the real `fetch` so chain-RPC calls
 * keep working untouched.
 *
 * The interceptor is disposable — `dispose()` restores the original
 * `fetch` and frees state. If an `AbortSignal` is supplied, intercepted
 * requests reject with an `AbortError` once aborted, which lets the
 * SDK's retry loops bail out promptly.
 */

const RELAY_BASE_URL = 'https://api.vultisig.com/router';
const FAST_VAULT_BASE = 'https://api.vultisig.com/vault';

const MESSAGE_PARTS = 'message';
const START_PARTS = 'start';
const COMPLETE_PARTS = 'complete';
const SETUP_PARTS = 'setup-message';

/**
 * Per-session state. We keep things minimal — no TTLs are needed
 * because the lifetime is bounded by `dispose()`.
 */
class SessionState {
	constructor() {
		this.joined = new Set();
		this.completed = new Set();
		this.queues = new Map(); // partyId → message[]
		this.setup = new Map();  // messageId → text body
	}
}

class RelayState {
	constructor() {
		this.sessions = new Map();
	}
	session(id) {
		let s = this.sessions.get(id);
		if (!s) {
			s = new SessionState();
			this.sessions.set(id, s);
		}
		return s;
	}
	destroy() { this.sessions.clear(); }
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };
const TEXT_HEADERS = { 'Content-Type': 'text/plain' };

function jsonResponse(value) {
	return new Response(JSON.stringify(value ?? null), {
		status: 200,
		headers: JSON_HEADERS,
	});
}

function textResponse(text) {
	return new Response(text ?? '', { status: 200, headers: TEXT_HEADERS });
}

function emptyOk() {
	return new Response('', { status: 200 });
}

function notFound(reason) {
	return new Response(reason || 'Not found', { status: 404 });
}

function badRequest(reason) {
	return new Response(reason || 'Bad request', { status: 400 });
}

async function readBodyText(opts) {
	if (!opts || opts.body == null) return '';
	const b = opts.body;
	if (typeof b === 'string') return b;
	if (b instanceof ArrayBuffer) return new TextDecoder().decode(b);
	if (ArrayBuffer.isView(b)) return new TextDecoder().decode(b.buffer);
	if (typeof Blob !== 'undefined' && b instanceof Blob) return b.text();
	if (typeof FormData !== 'undefined' && b instanceof FormData) return '';
	return String(b);
}

function getMessageIdHeader(opts) {
	if (!opts || !opts.headers) return '';
	try {
		const h = new Headers(opts.headers);
		return h.get('message_id') || h.get('Message-Id') || '';
	} catch {
		return opts.headers.message_id || '';
	}
}

function tryParseJson(text, fallback) {
	if (!text) return fallback;
	try { return JSON.parse(text); }
	catch { return fallback; }
}

/**
 * Dispatch a single relay request against `state` and return a Response.
 * `routerPath` is the path *under* the configured relay base (i.e. with
 * the `/router` prefix already stripped).
 */
async function dispatchRelay(state, routerPath, opts, log) {
	const method = (opts.method || (opts.body != null ? 'POST' : 'GET')).toUpperCase();
	const parts = routerPath.split('/').filter(Boolean);
	if (parts.length === 0) return notFound('empty relay path');

	const messageId = getMessageIdHeader(opts);
	const bodyText = await readBodyText(opts);

	if (parts[0] === MESSAGE_PARTS) {
		const sessionId = parts[1];
		const partyId = parts[2];
		const hash = parts[3];
		if (!sessionId) return notFound('missing sessionId');
		const sess = state.session(sessionId);

		if (method === 'POST' && !partyId) {
			const msg = tryParseJson(bodyText, null);
			if (!msg || typeof msg !== 'object') {
				return badRequest('message body must be JSON object');
			}
			const recipients = Array.isArray(msg.to)
				? msg.to
				: msg.to ? [msg.to] : [];
			if (recipients.length === 0) {
				log?.(`[relay] POST /message/${sessionId} no recipients — dropped`);
				return emptyOk();
			}
			for (const to of recipients) {
				if (!to) continue;
				const q = sess.queues.get(to) || [];
				q.push(msg);
				sess.queues.set(to, q);
			}
			return emptyOk();
		}

		if (method === 'GET' && partyId && !hash) {
			const q = sess.queues.get(partyId) || [];
			sess.queues.set(partyId, []);
			return jsonResponse(q);
		}

		if (method === 'DELETE' && partyId && hash) {
			const q = sess.queues.get(partyId) || [];
			sess.queues.set(partyId, q.filter(m => m && m.hash !== hash));
			return emptyOk();
		}
		return notFound(`unsupported message route ${method} /message/${parts.slice(1).join('/')}`);
	}

	if (parts[0] === START_PARTS) {
		const sessionId = parts[1];
		if (!sessionId) return notFound('missing sessionId');
		const sess = state.session(sessionId);
		if (method === 'POST') {
			const list = tryParseJson(bodyText, []);
			if (Array.isArray(list)) {
				for (const p of list) if (p) sess.joined.add(p);
			}
			return emptyOk();
		}
		if (method === 'GET') {
			return jsonResponse(Array.from(sess.joined));
		}
		return notFound(`unsupported start route ${method}`);
	}

	if (parts[0] === COMPLETE_PARTS) {
		const sessionId = parts[1];
		const isKeysign = parts[2] === 'keysign';
		if (!sessionId) return notFound('missing sessionId');
		const sess = state.session(sessionId);
		if (method === 'POST') {
			if (isKeysign) {
				log?.(`[relay] keysign complete on ${sessionId} (${messageId})`);
				return emptyOk();
			}
			const list = tryParseJson(bodyText, []);
			if (Array.isArray(list)) {
				for (const p of list) if (p) sess.completed.add(p);
			}
			return emptyOk();
		}
		if (method === 'GET') {
			return jsonResponse(Array.from(sess.completed));
		}
		return notFound(`unsupported complete route ${method}`);
	}

	if (parts[0] === SETUP_PARTS) {
		const sessionId = parts[1];
		if (!sessionId) return notFound('missing sessionId');
		const sess = state.session(sessionId);
		const key = messageId || '';
		if (method === 'POST') {
			sess.setup.set(key, bodyText || '');
			return emptyOk();
		}
		if (method === 'GET') {
			const v = sess.setup.get(key);
			if (v == null) return new Response('not yet uploaded', { status: 404 });
			return textResponse(v);
		}
		return notFound(`unsupported setup-message route ${method}`);
	}

	const sessionId = parts[0];
	const sess = state.session(sessionId);
	if (method === 'POST') {
		const list = tryParseJson(bodyText, []);
		if (Array.isArray(list)) {
			for (const p of list) if (p) sess.joined.add(p);
		}
		return emptyOk();
	}
	if (method === 'GET') {
		return jsonResponse(Array.from(sess.joined));
	}
	if (method === 'DELETE') {
		state.sessions.delete(sessionId);
		return emptyOk();
	}
	return notFound(`unsupported session route ${method} /${sessionId}`);
}

/**
 * Patch `globalThis.fetch` so every relay-bound request is served from
 * an in-memory match-maker. Pass-through everything else.
 *
 * Returns `{ baseUrl, dispose() }`. Always call `dispose()` (even on
 * abort) — leaving the fetch interceptor installed will make ordinary
 * HTTPS calls fail later in the session.
 *
 * Optional `signal` lets us short-circuit relay polls while the SDK's
 * `retry(...)` loops are still in-flight, so the keygen unwinds quickly
 * when the user cancels.
 *
 * Optional `log(message)` is invoked for diagnostic events (intercepted
 * URLs, blocked requests). Default is `console.debug`.
 */
export function installInMemoryMpcRelay({
	signal,
	baseUrl = RELAY_BASE_URL,
	fastVaultBase = FAST_VAULT_BASE,
	log = (msg) => console.debug('[mpc-relay]', msg),
} = {}) {
	const state = new RelayState();
	const original = globalThis.fetch;
	let restored = false;

	const baseNoTrailing = baseUrl.replace(/\/+$/, '');
	const baseWithSlash = baseNoTrailing + '/';
	const fastVaultNoTrailing = fastVaultBase.replace(/\/+$/, '');

	function urlOf(input) {
		if (typeof input === 'string') return input;
		if (input && typeof input === 'object' && 'url' in input) return input.url;
		return String(input);
	}

	function patched(input, opts = {}) {
		const reqUrl = urlOf(input);

		if (signal?.aborted) {
			return Promise.reject(new DOMException('Aborted', 'AbortError'));
		}

		if (reqUrl === baseNoTrailing || reqUrl.startsWith(baseWithSlash)) {
			let routerPath;
			try {
				routerPath = reqUrl.slice(baseNoTrailing.length);
			} catch (err) {
				return Promise.resolve(new Response(`bad relay url: ${err.message}`, { status: 400 }));
			}
			log?.(`${(opts.method || (opts.body != null ? 'POST' : 'GET')).toUpperCase()} ${routerPath || '/'}`);
			return dispatchRelay(state, routerPath || '/', opts, log)
				.catch(err => new Response(`relay error: ${err.message}`, { status: 500 }));
		}

		if (
			reqUrl === fastVaultNoTrailing ||
			reqUrl.startsWith(fastVaultNoTrailing + '/import') ||
			reqUrl.startsWith(fastVaultNoTrailing + '/get/') ||
			reqUrl.startsWith(fastVaultNoTrailing + '/sign') ||
			reqUrl.startsWith(fastVaultNoTrailing + '/resend') ||
			reqUrl.startsWith(fastVaultNoTrailing + '/verify/')
		) {
			log?.(`blocked FastVault call (${reqUrl}) — local DKG only`);
			return Promise.resolve(new Response('', { status: 200 }));
		}

		return original.call(this, input, opts);
	}

	globalThis.fetch = patched;

	return {
		baseUrl: baseNoTrailing,
		dispose() {
			if (restored) return;
			restored = true;
			if (globalThis.fetch === patched) {
				globalThis.fetch = original;
			}
			state.destroy();
		},
	};
}
